import { LatLng } from "../model/LatLng";
import { GeometryType } from "../types/dtos/api/itinerary/itinerary.dto";

const isValidLongitude = ( value: number ): boolean => value >= -180 && value <= 180;
const isValidLatitude = ( value: number ): boolean => value >= -90 && value <= 90;

const isLikelyValidPath = ( path: readonly LatLng[] ): boolean => {
    if ( path.length === 0 ) {
        return true;
    }

    return path.every( ( point ) => isValidLatitude( point.lat ) && isValidLongitude( point.lng ) );
};

const decodePathWithPrecision = ( encoded: string, type: GeometryType | undefined, precisionFactor: number ): LatLng[] => {
    const len = encoded.length;
    let index = 0;
    const array: LatLng[] = [];
    let lat = 0;
    let lng = 0;
    let ele = 0;

    while ( index < len ) {
        let b: number;
        let shift = 0;
        let result = 0;

        do {
            b = encoded.charCodeAt( index++ ) - 63;
            result |= ( b & 0x1f ) << shift;
            shift += 5;
        } while ( b >= 0x20 );

        const deltaLat = ( result & 1 ) !== 0 ? ~( result >> 1 ) : result >> 1;
        lat += deltaLat;
        shift = 0;
        result = 0;

        do {
            b = encoded.charCodeAt( index++ ) - 63;
            result |= ( b & 0x1f ) << shift;
            shift += 5;
        } while ( b >= 0x20 );

        const deltaLon = ( result & 1 ) !== 0 ? ~( result >> 1 ) : result >> 1;
        lng += deltaLon;

        if ( type === GeometryType.THREE_DIMENSIONAL ) {
            shift = 0;
            result = 0;

            do {
                b = encoded.charCodeAt( index++ ) - 63;
                result |= ( b & 0x1f ) << shift;
                shift += 5;
            } while ( b >= 0x20 );

            const deltaEle = ( result & 1 ) !== 0 ? ~( result >> 1 ) : result >> 1;
            ele += deltaEle;
            array.push( new LatLng( lat * precisionFactor, lng * precisionFactor, ele / 100 ) );
        } else {
            array.push( new LatLng( lat * precisionFactor, lng * precisionFactor ) );
        }
    }

    return array;
};

export function DecodePath( encoded: string, type?: GeometryType, precision?: number ): LatLng[] {
    if ( precision !== undefined ) {
        return decodePathWithPrecision( encoded, type, Math.pow( 10, -precision ) );
    }

    const decodedWith1e5 = decodePathWithPrecision( encoded, type, 1e-5 );

    if ( isLikelyValidPath( decodedWith1e5 ) ) {
        return decodedWith1e5;
    }

    // Some providers return encoded polylines with 1e-6 precision.
    const decodedWith1e6 = decodePathWithPrecision( encoded, type, 1e-6 );
    if ( isLikelyValidPath( decodedWith1e6 ) ) {
        return decodedWith1e6;
    }

    return decodedWith1e5;
}
